/**
 * The sweets (spec 02 §7.3): cake slice, donut, the three sherbets (shaved ice heaped in a footed
 * glass dish, a spoon stuck in, as the C64 draws them), ice cream, icepop, cupcake (also the giant
 * bonus, drawn 2.6x), cone and flan.
 */

import { type Color, Matrix4, Vector3 } from 'three';
import { type MeshBuilder, place } from '../../kit/MeshBuilder';
import type { Palette } from '../../palette';
import { arcPts, type Builder, chain, extrude, lathe, rotZ, torus, tube } from './kit';

type Pt = readonly [number, number];

/** Lays an outline drawn in (x, -z) flat and extrudes it upward from `y0` to `y1`. */
function slab(
  mb: MeshBuilder, outline: readonly Pt[], y0: number, y1: number, side: Color, top: Color, grow = 1,
): void {
  const m = chain(new Matrix4().makeTranslation(0, (y0 + y1) / 2, 0), new Matrix4().makeRotationX(-Math.PI / 2),
    new Matrix4().makeScale(grow, grow, 1));
  extrude(mb, outline, y1 - y0, side, m, top, side);
}

/** A ring lying flat (axis y) at height `y`: the lip under a scoop, a rim of cream. */
function flatRing(mb: MeshBuilder, R: number, r: number, y: number, c: Color): void {
  torus(mb, R, r, c, chain(new Matrix4().makeTranslation(0, y, 0), new Matrix4().makeRotationX(Math.PI / 2)), 24, 8);
}

/** Tiny coloured sprinkles scattered on a dome of radius (rx, ry, rz) centred at height `cy`. */
function sprinkles(mb: MeshBuilder, p: Palette, rx: number, ry: number, rz: number, cy: number, n: number): void {
  const cols = ['shoe.1', 'bubTrim.1', 'leaf.2', 'cheek.1'];
  for (let i = 0; i < n; i++) {
    const a = -1.3 + (2.6 * ((i * 7) % n)) / n;
    const e = 0.25 + 0.9 * (((i * 3) % 5) / 5);
    const x = Math.sin(a) * Math.cos(e) * rx;
    const z = Math.cos(a) * Math.cos(e) * rz;
    const y = cy + Math.sin(e) * ry;
    mb.box(0.07, 0.025, 0.025, p.color(cols[i % cols.length] ?? 'shoe.1'), place(x, y, z, a, 1, 0, i * 1.3), true);
  }
}

/** A cherry with a short stalk, sitting at (x, y, z). */
function cherry(mb: MeshBuilder, p: Palette, x: number, y: number, z: number, r = 0.09): void {
  mb.blob(r, r, r, p.color('berry.1'), place(x, y + r * 0.8, z), { detail: 2 });
  mb.blob(r * 0.3, r * 0.25, r * 0.2, p.color('berry.2'), place(x - r * 0.4, y + r * 1.2, z + r * 0.8), { detail: 1 });
  const s = y + r * 1.6;
  tube(mb, [new Vector3(x, s, z), new Vector3(x + 0.04, s + 0.1, z), new Vector3(x + 0.1, s + 0.14, z)],
    [0.02, 0.018, 0.015], p.color('stalk.1'), 5);
}

/** Shaved ice heaped in a footed glass dish, a spoon stuck in, a cherry on top. */
function sherbet(mb: MeshBuilder, p: Palette, ice: string): void {
  lathe(mb, [[0, 0], [0.24, 0], [0.25, 0.03], [0.2, 0.05], [0.06, 0.08], [0.05, 0.18], [0.12, 0.22], [0.3, 0.29],
    [0.4, 0.38], [0.43, 0.43], [0.38, 0.45], [0, 0.45]], (seg) => p.color(seg >= 8 ? 'glass.2' : 'glass.1'), undefined,
  { sides: 22 });
  mb.blob(0.38, 0.3, 0.36, p.color(`${ice}.1`), place(0, 0.5, 0), { detail: 3, topColor: p.color(`${ice}.2`) });
  mb.blob(0.2, 0.14, 0.2, p.color(`${ice}.2`), place(0.02, 0.76, 0), { detail: 2 });
  tube(mb, [new Vector3(-0.12, 0.6, 0.05), new Vector3(-0.44, 0.98, 0.1)], [0.03, 0.03], p.color('metal.2'), 6);
  mb.blob(0.07, 0.04, 0.05, p.color('metal.2'), place(-0.46, 1.0, 0.1), { detail: 1 });
  cherry(mb, p, 0.06, 0.86, 0.04);
}

/** A waffle cone, point down, with a criss-cross of two tans. */
function waffleCone(mb: MeshBuilder, p: Palette, h: number, r: number): void {
  const prof: Pt[] = [[0, 0], [r * 0.25, h * 0.2], [r * 0.5, h * 0.4], [r * 0.75, h * 0.6], [r, h * 0.84],
    [r * 1.08, h], [r * 0.9, h * 1.02], [0, h * 1.02]];
  const sides = 16;
  lathe(mb, prof, (seg, k) => p.color(seg >= 4 ? 'waffle.2' : (seg + k) % 2 ? 'waffle.1' : 'waffle.2'), undefined, {
    sides, twist: prof.map((_, i) => (i < 5 ? (i * Math.PI) / sides : (4 * Math.PI) / sides)),
  });
}

export const SWEETS: Record<string, Builder> = {
  'cake slice': (mb, p) => {
    // A wedge of strawberry shortcake: sponge and cream in layers, a strawberry on the cream.
    const wedge: Pt[] = [[-0.56, -0.18], [0.46, -0.34], [0.42, 0.34]];
    const sponge = p.color('sponge.1');
    const cream = p.color('cream.1');
    slab(mb, wedge, 0, 0.2, sponge, sponge);
    slab(mb, wedge, 0.2, 0.27, cream, cream, 1.02);
    slab(mb, wedge, 0.27, 0.32, p.color('berry.1'), p.color('berry.1'), 1.01);
    slab(mb, wedge, 0.32, 0.52, sponge, sponge);
    slab(mb, wedge, 0.52, 0.62, cream, p.color('cream.2'), 1.03);
    mb.blob(0.12, 0.09, 0.12, p.color('cream.2'), place(0.22, 0.66, 0), { detail: 2 });
    lathe(mb, [[0, 0], [0.09, 0.03], [0.13, 0.1], [0.12, 0.17], [0.06, 0.2], [0, 0.2]], p.color('berry.1'),
      place(0.22, 0.7, 0.02), { sides: 12 });
    mb.blob(0.08, 0.03, 0.08, p.color('leaf.1'), place(0.22, 0.9, 0.02), { detail: 1 });
  },
  donut: (mb, p) => {
    // A ring standing up to face the camera: tan dough, pink icing over the front, sprinkles.
    const at = place(0, 0.48, 0);
    torus(mb, 0.3, 0.18, (n) => p.color(n.z > 0.05 ? 'icing.1' : n.z > -0.2 ? 'dough.2' : 'dough.1'), at, 28, 14);
    const cols = ['paper.0', 'bubTrim.1', 'shoe.1', 'leaf.2'];
    for (let i = 0; i < 12; i++) {
      const u = (i / 12) * Math.PI * 2 + 0.2;
      const v = 0.8 + (i % 3) * 0.25;
      const r = 0.3 + Math.cos(v) * 0.18;
      const m = chain(at, place(Math.cos(u) * r, Math.sin(u) * r, Math.sin(v) * 0.18 + 0.01, 0, 1, 0, u * 2 + i));
      mb.box(0.08, 0.025, 0.03, p.color(cols[i % cols.length] ?? 'paper.0'), m, true);
    }
  },
  'blue sherbet': (mb, p) => sherbet(mb, p, 'iceBlue'),
  'white sherbet': (mb, p) => sherbet(mb, p, 'iceWhite'),
  'yellow sherbet': (mb, p) => sherbet(mb, p, 'iceLemon'),
  'ice cream': (mb, p) => {
    // Three scoops piled on a cone: grape, vanilla, strawberry.
    waffleCone(mb, p, 0.36, 0.22);
    const scoops: Array<[string, number, number]> = [
      ['creamGrape', 0.47, 0.25], ['cream', 0.66, 0.23], ['creamPink', 0.83, 0.2],
    ];
    for (const [ramp, y, r] of scoops) {
      mb.blob(r, r * 0.9, r, p.color(`${ramp}.1`), place(0, y, 0), { detail: 3, topColor: p.color(`${ramp}.2`) });
      flatRing(mb, r * 0.92, 0.05, y - r * 0.55, p.color(`${ramp}.1`));
    }
    cherry(mb, p, 0.02, 0.97, 0.02, 0.07);
  },
  icepop: (mb, p) => {
    // A strawberry ice lolly on a wooden stick, one bite gone from the corner.
    mb.box(0.1, 0.36, 0.06, p.color('wood.1'), place(0, 0, 0));
    const top = arcPts(0.02, 0.68, 0.25, 0.26, 0, Math.PI, 10).slice(1);
    const outline: Pt[] = [[-0.26, 0.3], [0.26, 0.3], [0.27, 0.34], [0.27, 0.68], ...top, [-0.27, 0.34]];
    extrude(mb, outline, 0.22, p.color('icepop.1'), undefined, p.color('icepop.1'));
    mb.blob(0.035, 0.1, 0.02, p.color('icepop.2'), rotZ(-0.17, 0.74, 0.35, 0.11), { detail: 1 });
    mb.blob(0.14, 0.03, 0.12, p.color('icepop.2'), place(0, 0.3, 0), { detail: 1 });
  },
  cupcake: (mb, p) => {
    // A pleated paper cup under a dome of white frosting that drips over it, sprinkles, a cherry.
    lathe(mb, [[0, 0], [0.28, 0], [0.3, 0.02], [0.4, 0.4], [0, 0.4]], (_, k) => p.color(k % 2 ? 'cup.1' : 'cup.2'),
      undefined, { sides: 24 });
    mb.blob(0.47, 0.3, 0.47, p.color('cream.1'), place(0, 0.46, 0), { detail: 3, topColor: p.color('cream.2') });
    for (let i = 0; i < 9; i++) {
      const a = -1.6 + (i / 8) * 3.2;
      const at = place(Math.sin(a) * 0.41, 0.36, Math.cos(a) * 0.41, a);
      mb.blob(0.08, 0.1 + (i % 2) * 0.04, 0.06, p.color('cream.1'), at, { detail: 2 });
    }
    mb.blob(0.26, 0.16, 0.26, p.color('cream.2'), place(0, 0.72, 0), { detail: 2 });
    sprinkles(mb, p, 0.44, 0.3, 0.44, 0.46, 14);
    cherry(mb, p, 0, 0.84, 0.02, 0.1);
  },
  cone: (mb, p) => {
    // One big chocolate scoop on a waffle cone.
    waffleCone(mb, p, 0.52, 0.28);
    mb.blob(0.34, 0.3, 0.34, p.color('choc.1'), place(0, 0.72, 0), { detail: 3, topColor: p.color('choc.2') });
    flatRing(mb, 0.3, 0.07, 0.56, p.color('choc.1'));
    mb.blob(0.07, 0.05, 0.04, p.color('choc.2'), place(-0.14, 0.9, 0.24), { detail: 1 });
  },
  flan: (mb, p) => {
    // Custard pudding on a plate: caramel on top running down the sides, cream and a cherry.
    lathe(mb, [[0, 0], [0.46, 0], [0.5, 0.04], [0.46, 0.06], [0, 0.06]], p.color('cream.2'), undefined, { sides: 24 });
    lathe(mb, [[0, 0.06], [0.38, 0.06], [0.37, 0.12], [0.3, 0.44], [0.26, 0.5], [0.18, 0.52], [0, 0.52]],
      (seg) => p.color(seg >= 3 ? 'caramel.1' : 'custard.1'), undefined, { sides: 24 });
    for (let i = 0; i < 7; i++) {
      const a = -1.3 + (i / 6) * 2.6;
      const len = 0.06 + ((i * 5) % 3) * 0.03;
      mb.blob(0.05, len, 0.04, p.color('caramel.1'), place(Math.sin(a) * 0.3, 0.44 - len * 0.5, Math.cos(a) * 0.3, a),
        { detail: 1 });
    }
    mb.blob(0.14, 0.08, 0.14, p.color('cream.2'), place(0, 0.56, 0), { detail: 2 });
    cherry(mb, p, 0, 0.6, 0.02, 0.08);
  },
};
